import { ApolloError, useQuery } from "@apollo/client";
import { LIST_CONVERSATIONS_QUERY } from "../graphql/queries";
import type { ConversationListItem } from "../types/conversation";
import type { ListConversationsQuery } from "../generated/graphql";
import { debugError, debugInfo } from "../utils/debug";
import { isStorybook } from "../utils/environment";

interface UseConversationsOptions {
  skip?: boolean;
  limit?: number;
  disablePolling?: boolean;
}

interface UseConversationsReturn {
  conversations: ConversationListItem[];
  hasConversations: boolean;
  loading: boolean;
  error: ApolloError | undefined;
  refetch: () => void;
  totalCount: number;
}

export const useConversations = (options: UseConversationsOptions = {}): UseConversationsReturn => {
  const { skip = false, limit = 50, disablePolling = false } = options;
  
  // Disable polling in Storybook environment to prevent mock exhaustion
  const shouldDisablePolling = disablePolling || isStorybook();

  const { data, loading, error, refetch } = useQuery<ListConversationsQuery>(LIST_CONVERSATIONS_QUERY, {
    variables: {
      limit,
    },
    skip,
    errorPolicy: "all",
    pollInterval: shouldDisablePolling ? 0 : 30000, // Refresh every 30 seconds unless disabled
    onError: error => {
      debugError("Error getting conversations:", error);
    },
    onCompleted: data => {
      debugInfo("Conversations query completed:", data);
    },
  });

  // Transform the API response into ConversationListItem format
  const conversations: ConversationListItem[] = [];

  if (data?.listConversations?.conversations) {
    data.listConversations.conversations.forEach(conv => {
      conversations.push({
        id: conv.id,
        title: conv.title || "Untitled Conversation",
        messageCount: typeof conv.messageCount === 'number' ? conv.messageCount : 0,
        createdAt: conv.createdAt,
        lastMessageAt: conv.lastMessageAt || undefined,
        status: (conv.status?.toLowerCase() as "active" | "archived") || "active",
        lastMessage: conv.lastMessageAt
          ? {
              content: "Continue your conversation...",
              timestamp: conv.lastMessageAt,
              senderRole: "agent" as const,
            }
          : undefined,
      });
    });
  }

  const hasConversations = conversations.length > 0;
  const totalCount = data?.listConversations?.totalCount || 0;

  return {
    conversations,
    hasConversations,
    loading,
    error,
    refetch,
    totalCount,
  };
};
